import React, { useEffect, useState } from 'react';
import PageRangeSelector from './PageRangeSelector';

const PrintForm = ({ document, errors, setFileInputs }) => {
  const [file, setFile] = useState(null);
  const [quantity, setQuantity] = useState(document.quantity || 1);
  const [paperFormat, setpaperFormat] = useState(document.paperFormat || 'A4');
  const [printOption, setPrintOption] = useState(document.printOption || 'Recto');
  const [printColor, setPrintColor] = useState(document.color || 'Black & White');
  const [paperOrientation, setPaperOrientation] = useState(document.paperOrientation || 'Portrait');
  const [addCoverPage, setAddCoverPage] = useState(document.addCoverPage || false);
  const [pageRange, setPageRange] = useState(document.pageRange || 'All Pages'); // 'All Pages' | 'x-y;z-w;...'
  const [additionalNotes, setAdditionalNotes] = useState(document.additionalNotes || '');
  const [paperFasteningChoice, setPaperFasteningChoice] = useState(document.paperFasteningChoice || 'None');
  const docNumber = document.docNumber;

  const paperFormats = [
    { name: 'A3', dimension: '297 x 420 mm', imageSrc: require('../assets/images/size_A3.avif') },
    { name: 'A4', dimension: '210 x 297 mm', imageSrc: require('../assets/images/size_A4.avif') },
    { name: 'A5', dimension: '148 x 210 mm', imageSrc: require('../assets/images/size_A5.avif') },
    { name: 'A6', dimension: '105 x 148 mm', imageSrc: require('../assets/images/size_A6.avif') },
  ];

  const printColors = [
    { name: 'Black & White', imageSrc: require('../assets/images/black-white.png') },
    { name: 'Color', imageSrc: require('../assets/images/color.jpg') }
  ];

  const printOptions = [
    { name: 'Recto', imageSrc: require('../assets/images/recto.avif') },
    { name: 'Recto-Verso', imageSrc: require('../assets/images/recto-verso.avif') }
  ];

  const paperOrientations = [
    { name: 'Portrait', imageSrc: require('../assets/images/orientation-portrait.png') },
    { name: 'Landscape', imageSrc: require('../assets/images/orientation-landscape.png') }
  ];

  const paperFastening = [
      {
        name: "None",
        imageSrc: require('../assets/images/without_bindings.avif')
      },
      {
          name: "Staple",
          imageSrc: require('../assets/images/staple.jpg')
      },
      {
          name: "Stitch",
          imageSrc: require('../assets/images/stitch.jpeg')
      },
      {
          name: "Glue Binding",
          imageSrc: require('../assets/images/glue_binding.jpg')
      },
      {
          name: "Spiral Binding",
          imageSrc: require('../assets/images/spiral_binding.jpg')
      },
      {
          name: "Comb Binding",
          imageSrc: require('../assets/images/comb_binding.jpg')
      }
  ];

  useEffect(() => {
    setFileInputs((fileInputs) => fileInputs.map((fileInput) => {
      if (fileInput.docNumber === docNumber) {
        return {
          ...fileInput,
          file,
          quantity,
          paperFormat,
          printOption,
          printColor,
          paperOrientation,
          addCoverPage,
          pageRange,
          additionalNotes,
          paperFasteningChoice
        };
      }
      return fileInput;
    }));
  }, [file, quantity, paperFormat, printOption, printColor, paperOrientation, addCoverPage, pageRange, additionalNotes, paperFasteningChoice]);

  return (
    <div className={`w-11/12 mx-auto my-3 p-6 bg-slate-100 rounded-lg shadow-md ${errors ? 'border border-red-500' : ''}`}>
      <h2 className="text-2xl font-semibold mb-4">Document N°: {docNumber}</h2>

      {/* Upload Section */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold mb-2"><span className='text-red-400'>*</span> Document:</h2>
        {!file && document.file && (
          <div className="mb-4">
            <p>Current file: {document.file.name}</p>
          </div>
        )}
        <input
          type="file"
          required
          className="mb-6 p-2 border rounded-md w-full"
          onChange={(e) => setFile(e.target.files[0])}
        />
        <p className="text-gray-600 mb-4">Supported formats: PDF, DOCX</p>

        {/* Show file error */}
        {errors && errors.fileError && <p className="text-red-500 text-sm">{errors.fileError}</p>}
      </div>

      {/* Formats Section (A3, A4, A5, A6) */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold mb-2"><span className='text-red-400'>*</span> Paper Format:</h2>
        <div className="flex gap-2">
          {paperFormats.map((format, index) => (
            <div
              key={index}
              className={`text-center p-2 border border-gray-200 shadow-sm hover:cursor-pointer ${paperFormat === format.name ? 'bg-blue-500 text-white' : ''}`}
              onClick={() => setpaperFormat(format.name)}
            >
              <img src={format.imageSrc} alt={format.name} className="w-20 h-20 object-cover mx-auto" />
              <p className="text-bold text-sm">{format.name}</p>
              <p className="text-sm">{format.dimension}</p>
            </div>
          ))}
        </div>

        {/* Show print format error */}
        {errors && errors.paperFormatError && <p className="text-red-500 text-sm">{errors.paperFormatError}</p>}
      </div>

      {/* Recto / Recto-Verso Section */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold mb-2"><span className='text-red-400'>*</span> Printing Option:</h2>
        <div className="flex gap-2">
          {printOptions.map((option, index) => (
            <div
              key={index}
              className={`text-center p-2 border border-gray-200 shadow-sm hover:cursor-pointer ${printOption === option.name ? 'bg-blue-500 text-white' : ''}`}
              onClick={() => setPrintOption(option.name)}
            >
              <img src={option.imageSrc} alt={option.name} className="w-20 h-20 object-cover mx-auto" />
              <p className="text-sm">{option.name}</p>
            </div>
          ))}
        </div>

        {/* Show print option error */}
        {errors && errors.printOptionError && <p className="text-red-500 text-sm">{errors.printOptionError}</p>}
      </div>
      {/* Color Section */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold mb-2"><span className='text-red-400'>*</span> Color:</h2>
        <div className="flex gap-2">
          {printColors.map((color, index) => (
            <div
              key={index}
              className={`text-center p-2 border border-gray-200 shadow-sm hover:cursor-pointer ${printColor === color.name ? 'bg-blue-500 text-white' : ''}`}
              onClick={() => setPrintColor(color.name)}
            >
              <img src={color.imageSrc} alt={color.name} className="w-20 h-20 object-cover mx-auto" />
              <p className="text-sm">{color.name}</p>
            </div>
          ))}
        </div>
        {/* Show print color error */}
        {errors && errors.printColorError && <p className="text-red-500 text-sm">{errors.printColorError}</p>}
      </div>

      {/* Paper Orientation Section */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold mb-2"><span className='text-red-400'>*</span> Paper Orientation:</h2>
        <div className="flex gap-2">
          {paperOrientations.map((orientation, index) => (
            <div
              key={index}
              className={`text-center p-2 border border-gray-200 shadow-sm hover:cursor-pointer ${paperOrientation === orientation.name ? 'bg-blue-500 text-white' : ''}`}
              onClick={() => setPaperOrientation(orientation.name)}
            >
              <img src={orientation.imageSrc} alt={orientation.name} className="w-20 h-20 object-cover mx-auto" />
              <p className="text-sm">{orientation.name}</p>
            </div>
          ))}
        </div>
        {/* Show paper orientation error */}
        {errors && errors.paperOrientationError && <p className="text-red-500 text-sm">{errors.paperOrientationError}</p>}
      </div>

      {/* Add Cover Page Section */}
      <div className="mb-6 flex flex-row gap-5">
        <h2 className="text-lg font-semibold mb-2">Add Cover Page:</h2>
        <input
          type="checkbox"
          checked={addCoverPage}
          onChange={() => setAddCoverPage(!addCoverPage)}
          className="mr-2"
        />
      </div>

      {/* Page Range Section */}
      <PageRangeSelector pageRange={pageRange} setPageRange={setPageRange} />

      {/* Paper Fastening Section */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold mb-2">Paper Fastening:</h2>
        <div className="flex gap-2">
          {paperFastening.map((option, index) => (
            <div
              key={index}
              className={`text-center p-2 border border-gray-200 shadow-sm hover:cursor-pointer ${paperFasteningChoice === option.name ? 'bg-blue-500 text-white' : ''}`}
              onClick={() => setPaperFasteningChoice(option.name)}
            >
              <img src={option.imageSrc} alt={option.name} className="w-20 h-20 object-cover mx-auto" />
              <p className="text-sm">{option.name}</p>
            </div>
          ))}
        </div>
        {/* Show paper fastening error */}
        {errors && errors.paperFasteningError && <p className="text-red-500 text-sm">{errors.paperFasteningError}</p>}
      </div>

      {/* Additional Notes Section */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold mb-2">Additional Notes:</h2>
        <textarea
          className="p-2 border rounded-md w-full h-20"
          value={additionalNotes}
          placeholder="Enter any additional notes you see necessary."
          onChange={(e) => setAdditionalNotes(e.target.value)}
        />
      </div>


      {/* Quantity Section */}
      <div className="mb-6">
        <label className="block text-lg font-semibold mb-2" htmlFor="quantity"><span className='text-red-400'>*</span> Quantity:</label>
        <input
          type="number"
          id="quantity"
          className="p-2 border rounded-md w-20"
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
          min="1"
          required
        />

        {/* Show quantity error */}
        {errors && errors.quantityError && <p className="text-red-500 text-sm">{errors.quantityError}</p>}
      </div>
    </div>
  );
};

export default PrintForm;
